import React from "react";
import { useTranslation } from "@me8eon/translation";
import { RequestBreakdown } from "../../../services/dashboardApi";
import { fmt } from "../poData";

type Props = {
    label: string;
    tooltip?: string;
    data: RequestBreakdown;
    periodDays: number;
    isCustomPeriod: boolean;
    onViewBreakdown: () => void;
};

// Bar/dot colour per language bucket (matches the mock: English blue, Deutsch red, Others grey).
const BUCKET_COLOR: Record<string, string> = {
    english: "#5b8def",
    deutsch: "#ea1b0a",
    others: "#9ca3af",
};

const R = 42;
const C = 2 * Math.PI * R;

/** KPI-style card: a total + a per-language (English / Deutsch / Others) legend and pie chart. */
const RequestCard: React.FC<Props> = ({ label, tooltip, data, periodDays, isCustomPeriod, onViewBreakdown }) => {
    const t = useTranslation();
    const tc = (key: string) => t(`productDashboard.common.${key}`);
    const tr = (key: string) => t(`productDashboard.requestCard.${key}`);
    const clickable = data.byLanguage.length > 0;

    let acc = 0;
    return (
        <div
            className={`po-card po-kpi po-request-card${clickable ? " po-kpi--clickable" : ""}`}
            title={tooltip}
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? onViewBreakdown : undefined}
            onKeyDown={
                clickable
                    ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onViewBreakdown();
                          }
                      }
                    : undefined
            }
        >
            <div className="po-kpi__head">
                <span className="po-kpi__label">{label}</span>
            </div>

            <div className="po-feedback-pie__body">
                <div className="po-request-card__info">
                    <div className="po-kpi__value">{fmt(data.total)}</div>
                    <div className="po-kpi__meta">{tc(isCustomPeriod ? "for" : "last")} {periodDays} {tc(periodDays === 1 ? "day" : "days")}</div>
                </div>

                {clickable && (
                    <svg className="po-feedback-pie__chart" viewBox="0 0 120 120" width="88" height="88" role="img" aria-label={`${label} language breakdown`}>
                        {data.byLanguage.map((l) => {
                            const len = data.total ? (l.count / data.total) * C : 0;
                            const seg = (
                                <circle
                                    key={l.key}
                                    cx="60"
                                    cy="60"
                                    r={R}
                                    fill="none"
                                    stroke={BUCKET_COLOR[l.key] ?? "#9ca3af"}
                                    strokeWidth="14"
                                    strokeDasharray={`${len} ${C - len}`}
                                    strokeDashoffset={-acc}
                                    transform="rotate(-90 60 60)"
                                />
                            );
                            acc += len;
                            return seg;
                        })}
                    </svg>
                )}
            </div>

            <ul className="po-request-card__legend">
                {data.byLanguage.map((l) => (
                    <li key={l.key}>
                        <i style={{ background: BUCKET_COLOR[l.key] ?? "#9ca3af" }} />
                        {l.label}
                    </li>
                ))}
                {data.byLanguage.length === 0 && <li>{t("productDashboard.requestCard.empty")}</li>}
            </ul>

            {clickable && <span className="po-link po-link--cue">{tr("breakdown")}</span>}
        </div>
    );
};

export default RequestCard;

